const imageRequests = new WeakMap();
const preparations = new WeakMap();
const posters = new Map();

// Decode before revealing or switching frames. A failed request must settle too.
export function prepareImage(image) {
  if (!image) return Promise.resolve(false);
  if (imageRequests.has(image)) return imageRequests.get(image);

  const ready = new Promise((resolve) => {
    let settled = false;
    const finish = async () => {
      const source = image.currentSrc;
      try { await image.decode(); } catch { /* Check the current request below. */ }
      if (settled || !image.complete) return;
      // A resize can replace the responsive source while decoding is in flight.
      if (source !== image.currentSrc) {
        finish();
        return;
      }
      settled = true;
      image.removeEventListener("load", finish);
      image.removeEventListener("error", finish);
      resolve(image.complete && image.naturalWidth > 0);
    };
    image.addEventListener("load", finish);
    image.addEventListener("error", finish);
    image.loading = "eager";
    if (image.complete) finish();
  });
  imageRequests.set(image, ready);
  return ready;
}

export function preparePoster(video) {
  if (!video.poster) return Promise.resolve(false);
  if (!posters.has(video.poster)) {
    const image = new Image();
    image.decoding = "async";
    image.src = video.poster;
    posters.set(video.poster, prepareImage(image));
  }
  return posters.get(video.poster);
}

export function prepareMedia(frame) {
  if (preparations.has(frame)) return preparations.get(frame);
  const image = frame.querySelector("img");
  const video = frame.querySelector("video[poster]");
  const ready = (image ? prepareImage(image) : video ? preparePoster(video) : Promise.resolve(false))
    .then((loaded) => {
      frame.classList.add(loaded ? "is-image-ready" : "has-image-error");
      if (!loaded) {
        const message = document.createElement("p");
        message.className = "media-error";
        message.textContent = "Image couldn’t load.";
        frame.append(message);
      }
      return loaded;
    });
  preparations.set(frame, ready);
  return ready;
}

export function initMediaReveals(scope = document.querySelector(".case-page")) {
  if (!scope || scope.dataset.mediaInitialized) return prepareMedia;
  scope.dataset.mediaInitialized = "true";
  const registered = new WeakSet();

  // Warm the next screen of images without competing with the opening poster.
  const warmup = "IntersectionObserver" in window && new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      prepareMedia(entry.target);
      warmup.unobserve(entry.target);
    }
  }, { rootMargin: `${Math.min(innerHeight * 1.25, 1200)}px 0px` });

  const arrival = "IntersectionObserver" in window && new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      entry.target.classList.add("is-in-view");
      prepareMedia(entry.target);
      arrival.unobserve(entry.target);
    }
  }, { threshold: 0.01 });

  function register(asset) {
    if (asset.closest("[data-media-reveal='off'], dialog, button")) return;
    const hero = asset.closest(".case-loop, [data-media-priority]");
    if (hero) {
      if (asset.tagName === "IMG") {
        asset.fetchPriority = "high";
        prepareImage(asset);
      } else preparePoster(asset);
      return; // The first image is immediately visible, with no entrance delay.
    }
    if (asset.matches("[aria-hidden='true']")) return;
    const nativeStage = asset.closest("[data-native-story] .breadth-stage");
    const frame = nativeStage || asset.closest("[data-media-reveal], [data-library-frame], .bondi-use-case-media, figure") || asset.parentElement;
    if (registered.has(frame)) return;
    registered.add(frame);
    const custom = asset.tagName === "VIDEO" || frame.querySelector("canvas, [data-cloud-parallax]")
      || frame.matches("[data-library-frame], .bondi-use-case-media");
    frame.dataset.mediaReveal = custom ? "container" : "image";
    if (!custom) asset.dataset.mediaImage = "";

    // Don't hide an entire chapter while its image downloads.
    const chapter = frame.closest(".story-chapter[data-reveal]");
    if (chapter) {
      chapter.removeAttribute("data-reveal");
      chapter.querySelector(".chapter-copy")?.setAttribute("data-reveal", "");
    }
    frame.removeAttribute("data-reveal");
    frame.classList.add("media-enhanced");
    if (warmup && arrival) {
      warmup.observe(frame);
      arrival.observe(frame);
    } else {
      frame.classList.add("is-in-view");
      prepareMedia(frame);
    }
  }

  const scan = (node) => {
    if (!(node instanceof Element)) return;
    if (node.matches("img, video[poster]")) register(node);
    node.querySelectorAll("img, video[poster]").forEach(register);
  };
  scan(scope);
  // Future cards inserted by a page component inherit the same default.
  const additions = new MutationObserver((records) => {
    for (const record of records) record.addedNodes.forEach(scan);
  });
  additions.observe(scope, { childList: true, subtree: true });
  return prepareMedia;
}
